import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enDashboard from '../locales/en/dashboard.json';
import enPos from '../locales/en/pos.json';
import enInventory from '../locales/en/inventory.json';
import enCustomers from '../locales/en/customers.json';
import enTransactions from '../locales/en/transactions.json';
import enReports from '../locales/en/reports.json';
import enExpenses from '../locales/en/expenses.json';
import enUsers from '../locales/en/users.json';
import enSettings from '../locales/en/settings.json';
import enAudit from '../locales/en/audit.json';
import enCash from '../locales/en/cash.json';
import enLanding from '../locales/en/landing.json';
import enCompanyProfile from '../locales/en/companyProfile.json';
import enContactUs from '../locales/en/contactUs.json';
import enPrivacyPolicy from '../locales/en/privacyPolicy.json';
import enTermsConditions from '../locales/en/termsConditions.json';
import enCookieConsent from '../locales/en/cookieConsent.json';
import enStockAudit from '../locales/en/stockAudit.json';
import enPurchaseOrders from '../locales/en/purchaseOrders.json';
import enPromotions from '../locales/en/promotions.json';

import idCommon from '../locales/id/common.json';
import idAuth from '../locales/id/auth.json';
import idDashboard from '../locales/id/dashboard.json';
import idPos from '../locales/id/pos.json';
import idInventory from '../locales/id/inventory.json';
import idCustomers from '../locales/id/customers.json';
import idTransactions from '../locales/id/transactions.json';
import idReports from '../locales/id/reports.json';
import idExpenses from '../locales/id/expenses.json';
import idUsers from '../locales/id/users.json';
import idSettings from '../locales/id/settings.json';
import idAudit from '../locales/id/audit.json';
import idCash from '../locales/id/cash.json';
import idLanding from '../locales/id/landing.json';
import idCompanyProfile from '../locales/id/companyProfile.json';
import idContactUs from '../locales/id/contactUs.json';
import idPrivacyPolicy from '../locales/id/privacyPolicy.json';
import idTermsConditions from '../locales/id/termsConditions.json';
import idCookieConsent from '../locales/id/cookieConsent.json';
import idStockAudit from '../locales/id/stockAudit.json';
import idPurchaseOrders from '../locales/id/purchaseOrders.json';
import idPromotions from '../locales/id/promotions.json';

// Translation resources
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    pos: enPos,
    inventory: enInventory,
    customers: enCustomers,
    transactions: enTransactions,
    reports: enReports,
    expenses: enExpenses,
    users: enUsers,
    settings: enSettings,
    audit: enAudit,
    cash: enCash,
    landing: enLanding,
    companyProfile: enCompanyProfile,
    contactUs: enContactUs,
    privacyPolicy: enPrivacyPolicy,
    termsConditions: enTermsConditions,
    cookieConsent: enCookieConsent,
    stockAudit: enStockAudit,
    purchaseOrders: enPurchaseOrders,
    promotions: enPromotions,
  },
  id: {
    common: idCommon,
    auth: idAuth,
    dashboard: idDashboard,
    pos: idPos,
    inventory: idInventory,
    customers: idCustomers,
    transactions: idTransactions,
    reports: idReports,
    expenses: idExpenses,
    users: idUsers,
    settings: idSettings,
    audit: idAudit,
    cash: idCash,
    landing: idLanding,
    companyProfile: idCompanyProfile,
    contactUs: idContactUs,
    privacyPolicy: idPrivacyPolicy,
    termsConditions: idTermsConditions,
    cookieConsent: idCookieConsent,
    stockAudit: idStockAudit,
    purchaseOrders: idPurchaseOrders,
    promotions: idPromotions,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: localStorage.getItem('i18nextLng') || 'id',
    resources,
    fallbackLng: 'id',
    debug: process.env.NODE_ENV === 'development',

    // Language detection options
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React already does escaping
    },

    // Namespace configuration
    defaultNS: 'common',
    ns: [
      'common',
      'auth',
      'dashboard',
      'pos',
      'inventory',
      'customers',
      'transactions',
      'reports',
      'expenses',
      'users',
      'settings',
      'audit',
      'cash',
      'landing',
      'companyProfile',
      'contactUs',
      'privacyPolicy',
      'termsConditions',
      'cookieConsent',
      'stockAudit',
      'purchaseOrders',
      'promotions',
    ],

    // Return key if translation is missing in development
    returnEmptyString: false,
    returnNull: false,
  });

// Custom language change handler to sync with system settings
export const changeLanguage = async (lng: string) => {
  await i18n.changeLanguage(lng);
  // Store the language preference
  localStorage.setItem('i18nextLng', lng);
};

// Get current language
export const getCurrentLanguage = () => i18n.language || 'id';

// Get supported languages
export const getSupportedLanguages = () => ['en', 'id'];

export default i18n;
